import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import QRCode from 'qrcode';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const PayInvoice = () => {
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [companySettings, setCompanySettings] = useState(null);
  const [usdcData, setUsdcData] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingUSDC, setLoadingUSDC] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';

  useEffect(() => {
    console.log('PayInvoice: useEffect running');
    fetchInvoices();
    fetchCompanySettings();
  }, []);

  const fetchInvoices = async () => {
    console.log('PayInvoice: fetchInvoices called');
    try {
      const token = localStorage.getItem('token');
      console.log('PayInvoice: Making API call to', `${API}/invoices`);
      const response = await axios.get(`${API}/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('PayInvoice: Received invoices:', response.data.length);
      
      // Filter for unpaid and pending invoices
      let unpaidInvoices = response.data.filter(inv => 
        inv.payment_status === 'unpaid' || inv.payment_status === 'pending'
      );
      
      // For agencies: Show ALL unpaid invoices - no special filtering
      // Backend already filters by agency_id
      
      console.log('PayInvoice: Filtered unpaid/pending:', unpaidInvoices.length);
      setInvoices(unpaidInvoices);
    } catch (error) {
      console.error('PayInvoice: Error fetching invoices:', error);
      if (toast && toast.error) {
        toast.error('Failed to load invoices');
      } else {
        console.error('Toast not available');
      }
    }
  };

  const fetchCompanySettings = async () => {
    console.log('PayInvoice: fetchCompanySettings called');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/company-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('PayInvoice: Company settings loaded');
      setCompanySettings(response.data);
    } catch (error) {
      console.error('PayInvoice: Failed to load company settings:', error);
    }
  };

  const handlePayClick = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentMethod('');
    setShowPaymentDialog(true);
    setUsdcData(null);
    setQrCodeUrl('');
    setTransactionId('');
  };

  const selectPaymentMethod = async (method) => {
    setPaymentMethod(method);
    
    if (method === 'qr_code') {
      // Generate EPC QR code for SEPA payments
      await generateEPCQRCode();
    } else if (method === 'crypto') {
      // Fetch USDC conversion
      await fetchUSDCConversion();
    }
  };

  const generateEPCQRCode = async () => {
    if (!companySettings || !selectedInvoice) return;
    
    // EPC QR Code format for SEPA payments
    const epcData = [
      'BCD',  // Service tag
      '002',  // Version
      '1',    // Character set (UTF-8)
      'SCT',  // Identification
      companySettings.swift_bic || '',  // BIC
      companySettings.company_name || '',  // Beneficiary name
      companySettings.iban || '',  // Beneficiary account (IBAN)
      `EUR${(selectedInvoice.total_amount_with_vat || selectedInvoice.total_amount || 0).toFixed(2)}`,  // Amount
      '',     // Purpose
      '',     // Structured reference
      `Invoice ${selectedInvoice.invoice_number}`,  // Unstructured remittance
      ''      // Beneficiary to originator information
    ].join('\n');
    
    try {
      const qrUrl = await QRCode.toDataURL(epcData, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeUrl(qrUrl);
    } catch (error) {
      console.error('Failed to generate QR code:', error);
      toast.error('Failed to generate QR code');
    }
  };

  const fetchUSDCConversion = async () => {
    setLoadingUSDC(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/payment/eur-to-usdc?amount=${selectedInvoice.total_amount_with_vat || selectedInvoice.total_amount || 0}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsdcData(response.data);
      
      // Generate QR code for crypto wallet
      if (companySettings?.usdc_wallet_address) {
        const cryptoData = `ethereum:${companySettings.usdc_wallet_address}?value=${response.data.usdc_amount}&token=USDC`;
        const qrUrl = await QRCode.toDataURL(cryptoData, {
          width: 300,
          margin: 2,
          color: { dark: '#000000', light: '#FFFFFF' }
        });
        setQrCodeUrl(qrUrl);
      }
    } catch (error) {
      console.error('Failed to fetch USDC conversion:', error);
      toast.error('Failed to fetch conversion rate');
      setUsdcData(null);
    } finally {
      setLoadingUSDC(false);
    }
  };

  const initiatePayment = async () => {
    console.log('initiatePayment called');
    
    if (!paymentMethod) {
      toast.error('Please select a payment method');
      return;
    }
    
    if (paymentMethod === 'crypto' && !transactionId) {
      toast.error('Please enter transaction ID for crypto payment');
      return;
    }
    
    console.log('Setting loading to true');
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      console.log('Token:', token ? 'exists' : 'missing');
      
      // Send payment_method and transaction_id as query parameters (not body)
      const params = new URLSearchParams({
        payment_method: paymentMethod
      });
      if (transactionId) {
        params.append('transaction_id', transactionId);
      }
      
      const url = `${API}/invoices/${selectedInvoice.id}/initiate-payment?${params.toString()}`;
      console.log('Making request to:', url);
      
      const response = await axios.post(
        url,
        {},  // Empty body
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000  // 30 second timeout
        }
      );
      
      console.log('Payment response:', response.data);
      toast.success('Payment initiated successfully. Awaiting admin confirmation.');
      setShowPaymentDialog(false);
      await fetchInvoices();
      
    } catch (error) {
      console.error('Payment initiation error:', error);
      console.error('Error response:', error.response);
      
      let errorMsg = 'Failed to initiate payment';
      
      if (error.code === 'ECONNABORTED') {
        errorMsg = 'Request timeout - please try again';
      } else if (error.response) {
        errorMsg = error.response.data?.detail || error.response.statusText || errorMsg;
      } else if (error.request) {
        errorMsg = 'No response from server - check your connection';
      } else {
        errorMsg = error.message || errorMsg;
      }
      
      toast.error(errorMsg);
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const confirmPayment = async (invoiceId) => {
    if (!window.confirm('Confirm that payment has been received for this invoice?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/invoices/${invoiceId}/confirm-payment`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Payment confirmed successfully');
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to confirm payment');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0A1128 0%, #1E3A5F 100%)', padding: '2rem' }}>
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold mb-6" style={{ color: '#FFD700' }}>
          💳 Pay Invoices
        </h1>

        {invoices.length === 0 ? (
          <Card style={{ background: '#1E2A4A', borderColor: '#4A5A8A', padding: '2rem', textAlign: 'center' }}>
            <p style={{ color: '#B0B0B0' }}>No unpaid invoices found</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {invoices.map((invoice) => (
              <Card key={invoice.id} style={{ background: '#1E2A4A', borderColor: '#4A5A8A', padding: '1.5rem' }}>
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-semibold" style={{ color: '#FFD700' }}>
                      Invoice #{invoice.invoice_number}
                    </h3>
                    <p style={{ color: '#B0B0B0' }}>{invoice.agency_name}</p>
                    <p className="text-2xl font-bold mt-2" style={{ color: '#E8E8E8' }}>
                      €{(invoice.total_amount_with_vat || invoice.total_amount || 0).toFixed(2)}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <span 
                        className="px-3 py-1 rounded text-xs font-semibold"
                        style={{
                          background: invoice.payment_status === 'unpaid' ? '#EF4444' : 
                                    invoice.payment_status === 'pending' ? '#F59E0B' : '#10B981',
                          color: '#FFFFFF'
                        }}
                      >
                        {invoice.payment_status === 'unpaid' ? '⚠️ Unpaid' :
                         invoice.payment_status === 'pending' ? '📤 Agency Marked as Paid' : '✅ Paid'}
                      </span>
                      {invoice.payment_method && (
                        <span className="px-3 py-1 rounded text-xs" style={{ background: '#4A5A8A', color: '#E8E8E8' }}>
                          {invoice.payment_method === 'bank_transfer' ? '🏦 Bank Transfer' :
                           invoice.payment_method === 'qr_code' ? '📱 QR Code' : '₿ Crypto'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {(invoice.payment_status === 'unpaid' || invoice.payment_status === 'pending') && !isAdmin && (
                      <Button 
                        onClick={() => handlePayClick(invoice)}
                        style={{ background: '#10B981', color: '#FFFFFF' }}
                      >
                        Pay Now
                      </Button>
                    )}
                    {invoice.payment_status === 'pending' && isAdmin && (
                      <Button 
                        onClick={() => confirmPayment(invoice.id)}
                        style={{ background: '#10B981', color: '#FFFFFF' }}
                      >
                        ✓ Confirm Payment
                      </Button>
                    )}
                  </div>
                </div>
                
                {invoice.payment_transaction_id && (
                  <div className="mt-3 pt-3" style={{ borderTop: '1px solid #4A5A8A' }}>
                    <p className="text-sm" style={{ color: '#B0B0B0' }}>
                      Transaction ID: <span style={{ color: '#E8E8E8' }}>{invoice.payment_transaction_id}</span>
                    </p>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Payment Method Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFD700' }}>
              Pay Invoice #{selectedInvoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div style={{ background: '#0A1128', padding: '1rem', borderRadius: '8px' }}>
              <p className="text-sm" style={{ color: '#B0B0B0' }}>Amount Due</p>
              <p className="text-3xl font-bold" style={{ color: '#FFD700' }}>
                €{(selectedInvoice?.total_amount_with_vat || selectedInvoice?.total_amount || 0).toFixed(2)}
              </p>
            </div>

            {!paymentMethod && (
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#E8E8E8' }}>
                  Select Payment Method:
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  <Button
                    onClick={() => selectPaymentMethod('bank_transfer')}
                    className="p-6 text-left h-auto"
                    style={{ background: '#2D3748', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-3xl">🏦</span>
                      <div>
                        <p className="font-semibold text-lg">Bank Transfer</p>
                        <p className="text-sm" style={{ color: '#B0B0B0' }}>Transfer to our bank account</p>
                      </div>
                    </div>
                  </Button>

                  <Button
                    onClick={() => selectPaymentMethod('qr_code')}
                    className="p-6 text-left h-auto"
                    style={{ background: '#2D3748', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-3xl">📱</span>
                      <div>
                        <p className="font-semibold text-lg">QR Code Payment</p>
                        <p className="text-sm" style={{ color: '#B0B0B0' }}>Scan with your banking app (SEPA)</p>
                      </div>
                    </div>
                  </Button>

                  <Button
                    onClick={() => selectPaymentMethod('crypto')}
                    className="p-6 text-left h-auto"
                    style={{ background: '#2D3748', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-3xl">₿</span>
                      <div>
                        <p className="font-semibold text-lg">Crypto Payment (USDC)</p>
                        <p className="text-sm" style={{ color: '#B0B0B0' }}>Pay with USDC cryptocurrency</p>
                      </div>
                    </div>
                  </Button>
                </div>
              </div>
            )}

            {/* Bank Transfer Details */}
            {paymentMethod === 'bank_transfer' && companySettings && (
              <div className="space-y-4">
                <Button
                  onClick={() => setPaymentMethod('')}
                  variant="outline"
                  style={{ borderColor: '#4A5A8A', color: '#E8E8E8' }}
                >
                  ← Back
                </Button>
                <div style={{ background: '#0A1128', padding: '1.5rem', borderRadius: '8px' }}>
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#FFD700' }}>
                    🏦 Bank Transfer Details
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Beneficiary</p>
                      <p className="font-semibold" style={{ color: '#E8E8E8' }}>{companySettings.company_name}</p>
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Bank Name</p>
                      <p className="font-semibold" style={{ color: '#E8E8E8' }}>{companySettings.bank_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>IBAN</p>
                      <p className="font-semibold" style={{ color: '#E8E8E8' }}>{companySettings.iban || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>SWIFT/BIC</p>
                      <p className="font-semibold" style={{ color: '#E8E8E8' }}>{companySettings.swift_bic || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Amount</p>
                      <p className="text-xl font-bold" style={{ color: '#FFD700' }}>€{(selectedInvoice?.total_amount_with_vat || selectedInvoice?.total_amount || 0).toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Reference</p>
                      <p className="font-semibold" style={{ color: '#E8E8E8' }}>Invoice {selectedInvoice?.invoice_number}</p>
                    </div>
                  </div>
                  <div className="mt-6 p-3 rounded" style={{ background: '#2D3748' }}>
                    <p className="text-sm" style={{ color: '#B0B0B0' }}>
                      ⚠️ Please include the invoice number in the transfer reference
                    </p>
                  </div>
                </div>
                <Button
                  onClick={initiatePayment}
                  disabled={loading}
                  className="w-full"
                  style={{ background: '#10B981', color: '#FFFFFF' }}
                >
                  {loading ? 'Processing...' : 'I Have Made the Transfer'}
                </Button>
              </div>
            )}

            {/* QR Code Payment */}
            {paymentMethod === 'qr_code' && (
              <div className="space-y-4">
                <Button
                  onClick={() => setPaymentMethod('')}
                  variant="outline"
                  style={{ borderColor: '#4A5A8A', color: '#E8E8E8' }}
                >
                  ← Back
                </Button>
                <div style={{ background: '#0A1128', padding: '1.5rem', borderRadius: '8px', textAlign: 'center' }}>
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#FFD700' }}>
                    📱 Scan with Your Banking App
                  </h3>
                  {qrCodeUrl && (
                    <div className="flex justify-center mb-4">
                      <img src={qrCodeUrl} alt="Payment QR Code" style={{ border: '4px solid white', borderRadius: '8px' }} />
                    </div>
                  )}
                  <p className="text-sm mb-4" style={{ color: '#B0B0B0' }}>
                    Scan this QR code with your mobile banking app to automatically fill in the payment details
                  </p>
                  <div className="text-left mt-4 p-3 rounded" style={{ background: '#2D3748' }}>
                    <p className="text-sm font-semibold mb-2" style={{ color: '#E8E8E8' }}>Payment Info:</p>
                    <p className="text-sm" style={{ color: '#B0B0B0' }}>
                      Amount: <span style={{ color: '#FFD700' }}>€{(selectedInvoice?.total_amount_with_vat || selectedInvoice?.total_amount || 0).toFixed(2)}</span>
                    </p>
                    <p className="text-sm" style={{ color: '#B0B0B0' }}>
                      Reference: <span style={{ color: '#E8E8E8' }}>Invoice {selectedInvoice?.invoice_number}</span>
                    </p>
                  </div>
                </div>
                <Button
                  onClick={initiatePayment}
                  disabled={loading}
                  className="w-full"
                  style={{ background: '#10B981', color: '#FFFFFF' }}
                >
                  {loading ? 'Processing...' : 'I Have Scanned and Paid'}
                </Button>
              </div>
            )}

            {/* Crypto Payment */}
            {paymentMethod === 'crypto' && (
              <div className="space-y-4">
                <Button
                  onClick={() => setPaymentMethod('')}
                  variant="outline"
                  style={{ borderColor: '#4A5A8A', color: '#E8E8E8' }}
                >
                  ← Back
                </Button>
                <div style={{ background: '#0A1128', padding: '1.5rem', borderRadius: '8px' }}>
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#FFD700' }}>
                    ₿ USDC Cryptocurrency Payment
                  </h3>
                  
                  {loadingUSDC ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400 mx-auto mb-4"></div>
                      <p style={{ color: '#B0B0B0' }}>Loading conversion rate...</p>
                    </div>
                  ) : usdcData ? (
                    <>
                      <div className="space-y-4 mb-4">
                        <div style={{ background: '#2D3748', padding: '1rem', borderRadius: '8px' }}>
                          <p className="text-sm mb-2" style={{ color: '#B0B0B0' }}>Amount in EUR</p>
                          <p className="text-2xl font-bold" style={{ color: '#E8E8E8' }}>€{usdcData.eur_amount.toFixed(2)}</p>
                        </div>
                        <div style={{ background: '#2D3748', padding: '1rem', borderRadius: '8px' }}>
                          <p className="text-sm mb-2" style={{ color: '#B0B0B0' }}>Amount in USDC (Rounded Up)</p>
                          <p className="text-2xl font-bold" style={{ color: '#10B981' }}>{usdcData.usdc_amount} USDC</p>
                          <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>{usdcData.rate_description}</p>
                        </div>
                      </div>

                      {qrCodeUrl && (
                        <div className="text-center mb-4">
                          <img src={qrCodeUrl} alt="Crypto Wallet QR" className="mx-auto" style={{ border: '4px solid white', borderRadius: '8px', width: '300px' }} />
                        </div>
                      )}

                      <div style={{ background: '#2D3748', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
                        <p className="text-sm mb-2" style={{ color: '#B0B0B0' }}>Wallet Address</p>
                        <p className="font-mono text-sm break-all" style={{ color: '#E8E8E8' }}>
                          {companySettings?.usdc_wallet_address || 'Not configured'}
                        </p>
                      </div>

                      <div className="mb-4">
                        <label className="text-sm mb-2 block" style={{ color: '#E8E8E8' }}>
                          Transaction ID / Hash *
                        </label>
                        <input
                          type="text"
                          value={transactionId}
                          onChange={(e) => setTransactionId(e.target.value)}
                          placeholder="Enter transaction ID after sending USDC"
                          className="w-full p-2 rounded"
                          style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8', border: '1px solid' }}
                        />
                        <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                          Enter the blockchain transaction ID after completing the transfer
                        </p>
                      </div>

                      <div className="p-3 rounded mb-4" style={{ background: '#EF4444', color: '#FFFFFF' }}>
                        <p className="text-sm font-semibold">⚠️ Important:</p>
                        <ul className="text-xs mt-2 space-y-1">
                          <li>• Send exactly {usdcData.usdc_amount} USDC</li>
                          <li>• Use Ethereum network (ERC-20)</li>
                          <li>• Payment will be confirmed by admin</li>
                          <li>• Save your transaction ID</li>
                        </ul>
                      </div>

                      <Button
                        onClick={initiatePayment}
                        disabled={loading || !transactionId}
                        className="w-full"
                        style={{ background: '#10B981', color: '#FFFFFF' }}
                      >
                        {loading ? 'Processing...' : 'I Have Sent USDC'}
                      </Button>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-lg mb-4" style={{ color: '#EF4444' }}>⚠️ Unable to load conversion rate</p>
                      <p className="text-sm mb-6" style={{ color: '#B0B0B0' }}>
                        Please try again or contact support if the issue persists.
                      </p>
                      <Button
                        onClick={() => fetchUSDCConversion()}
                        className="mb-4"
                        style={{ background: '#4A5A8A', color: '#E8E8E8' }}
                      >
                        Retry
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayInvoice;
